/**
 * LinePicker — choose the extracted row a `price` / `quantity` link anchors to
 * (FR-017 #5).
 *
 * Every row is listed, in the document's order: a row that cannot be
 * recognised again after a re-extraction (no name, no number — the lib's
 * `line_fingerprint` gives it no identity) is shown DISABLED, not hidden.
 * Hiding it would make "Zeile 3" the third visible row rather than row 3, and
 * the number the farmer picked would no longer be the one the panel shows.
 *
 * Native radios: arrow keys move within the group and Space selects, with no
 * roving-tabindex code to get wrong.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { isAnchorableRow, rowSummary, type LineRow } from '../../components/twinLinks/lineRows';

interface LinePickerProps {
  rows: readonly LineRow[];
  /** The picked row's `anchor_ref`, or `null`. */
  selected: string | null;
  onSelect: (index: number) => void;
}

export const LinePicker: React.FC<LinePickerProps> = ({ rows, selected, onSelect }) => {
  const { t } = useTranslation();
  const name = React.useId();
  return (
    <fieldset role="radiogroup" aria-label={t('twinLinks.form.line')} className="document-twin-links__lines">
      <legend>{t('twinLinks.form.line')}</legend>
      {rows.map((row, index) => {
        const anchorable = isAnchorableRow(row);
        return (
          <label key={index} className="document-twin-links__line">
            <input
              type="radio"
              name={name}
              value={String(index)}
              checked={selected === String(index)}
              disabled={!anchorable}
              onChange={() => onSelect(index)}
            />
            {rowSummary(row, index)}
            {!anchorable && (
              <span className="document-twin-links__line-hint"> {t('twinLinks.form.lineNotIdentifiable')}</span>
            )}
          </label>
        );
      })}
    </fieldset>
  );
};

export default LinePicker;
